import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
} from "@mui/material";

import { toast } from "react-toastify";
import { apiput } from "../../service/api";
import { US_STATES } from "../../utils/intakeValidation";
import CustomFieldsEditor, {
  customFieldsToRows,
  rowsToCustomFields,
  validateCustomFieldRows,
} from "../../components/builderintakeforms/CustomFieldsEditor";
import LogoSelect from "../../components/builderintakeforms/LogoSelect";

/* -------------------- Helpers -------------------- */
const toTextarea = (arr = []) => arr.join("\n");
const parseTextarea = (text = "") =>
  String(text)
    .split("\n")
    .map((v) => v.trim())
    .filter(Boolean);

/* -------------------- Component -------------------- */
export default function BuilderIntakeEditModal({
  open,
  onClose,
  intakeData,
  onUpdate,
}) {
  const [formData, setFormData] = useState(null);

  // -----------------------------------
  // Load initial data
  // -----------------------------------
  useEffect(() => {
    if (!intakeData) return;

    setFormData({
      slug: intakeData.slug || "",
      title: intakeData.title || "",
      subtitle: intakeData.subtitle || "",
      intro: intakeData.intro || "",
      brandColor: intakeData.brandColor || "#b89b5e",
      endpoint: intakeData.endpoint || "lead/add",
      logoSrc: intakeData.logoSrc || "",

      emailToText: toTextarea(intakeData.emailTo),
      loanOfficersText: toTextarea(intakeData.loanOfficers),
      agentNamesText: toTextarea(intakeData.agentNames),
      communitiesText: toTextarea(intakeData.communities),
      forwardCommitmentsText: toTextarea(intakeData.forwardCommitments),
      urgencyText: toTextarea(intakeData.urgency),
      dateFieldsText: toTextarea(intakeData.dateFields),
       candidSlug: intakeData.candidSlug || "",
      state: intakeData.state || "",
      fieldLabelOverrides: {
        taxRate: intakeData.fieldLabelOverrides?.taxRate || "",
      },

      smsOptIn: {
        enabled: intakeData.smsOptIn?.enabled || false,
        consentText: intakeData.smsOptIn?.consentText || "",
      },

      customFieldRows: customFieldsToRows(intakeData.customFields),
      hiddenFields: intakeData.hiddenFields || [],

      isActive: intakeData.isActive ?? true,
    });
  }, [intakeData]);

  if (!formData) return null;

  // -----------------------------------
  // Submit PUT (JSON)
  // -----------------------------------
  const handleSubmit = async () => {
    try {
      const customFieldsError = validateCustomFieldRows(formData.customFieldRows);
      if (customFieldsError) {
        toast.error(customFieldsError);
        return;
      }

      const payload = {
        slug: formData.slug,
        title: formData.title,
        subtitle: formData.subtitle,
        intro: formData.intro,
        brandColor: formData.brandColor,
        endpoint: formData.endpoint,
        logoSrc: formData.logoSrc,
        isActive: formData.isActive,

        emailTo: parseTextarea(formData.emailToText),
        loanOfficers: parseTextarea(formData.loanOfficersText),
        agentNames: parseTextarea(formData.agentNamesText),
        communities: parseTextarea(formData.communitiesText),
        forwardCommitments: parseTextarea(formData.forwardCommitmentsText),
        urgency: parseTextarea(formData.urgencyText),
        dateFields: parseTextarea(formData.dateFieldsText),
         candidSlug: String(formData.candidSlug || "").trim(),
        state: formData.state || "",
        fieldLabelOverrides: formData.fieldLabelOverrides,
        smsOptIn: formData.smsOptIn,
        customFields: rowsToCustomFields(formData.customFieldRows),
        hiddenFields: formData.hiddenFields,
      };

      const res = await apiput(`intake/${intakeData._id}`, payload);

      if (res?.status === 200) {
        toast.success("Builder intake updated");
        onUpdate();
        onClose();
      } else {
        toast.error("Failed to update intake");
      }
    } catch (err) {
      console.error(err);
      toast.error("Unexpected error updating intake");
    }
  };

  // -----------------------------------
  // Render
  // -----------------------------------
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Builder Intake</DialogTitle>

      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Slug"
              value={formData.slug}
              onChange={(e) =>
                setFormData({ ...formData, slug: e.target.value })
              }
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
            />
          </Grid>

           <Grid item xs={12} sm={6}>
            <TextField
  fullWidth
  label="Candid Slug"
  value={formData.candidSlug}
  onChange={(e) => setFormData({ ...formData, candidSlug: e.target.value })}
/>

          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="State"
              value={formData.state}
              onChange={(e) => setFormData({ ...formData, state: e.target.value })}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
              helperText="Admin-only — not shown on the borrower-facing form."
            >
              <option value="">Select a state</option>
              {US_STATES.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.name} ({s.code})
                </option>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Subtitle"
              value={formData.subtitle}
              onChange={(e) =>
                setFormData({ ...formData, subtitle: e.target.value })
              }
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Intro"
              value={formData.intro}
              onChange={(e) =>
                setFormData({ ...formData, intro: e.target.value })
              }
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              type="color"
              fullWidth
              label="Brand Color"
              value={formData.brandColor}
              onChange={(e) =>
                setFormData({ ...formData, brandColor: e.target.value })
              }
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Endpoint"
              value={formData.endpoint}
              onChange={(e) =>
                setFormData({ ...formData, endpoint: e.target.value })
              }
            />
          </Grid>

          {/* Logo */}
          <Grid item xs={12}>
            <LogoSelect
              value={formData.logoSrc}
              onChange={(url) => setFormData({ ...formData, logoSrc: url })}
            />
          </Grid>

          {/* Bulk Fields */}
          {[
            ["Email Recipients", "emailToText"],
         
            ["Loan Officers", "loanOfficersText"],
            ["Agent Names", "agentNamesText"],
            ["Communities", "communitiesText"],
            ["Forward Commitments", "forwardCommitmentsText"],
            ["Urgency", "urgencyText"],
            ["Date Fields", "dateFieldsText"],
          ].map(([label, key]) => (
            <Grid item xs={12} key={key}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                label={`${label} (one per line)`}
                value={formData[key]}
                onChange={(e) =>
                  setFormData({ ...formData, [key]: e.target.value })
                }
              />
            </Grid>
          ))}

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Tax Rate Label Override"
              value={formData.fieldLabelOverrides.taxRate}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  fieldLabelOverrides: {
                    taxRate: e.target.value,
                  },
                })
              }
            />
          </Grid>

          <CustomFieldsEditor
            rows={formData.customFieldRows}
            onRowsChange={(rows) => setFormData({ ...formData, customFieldRows: rows })}
            hiddenFields={formData.hiddenFields}
            onHiddenFieldsChange={(hf) => setFormData({ ...formData, hiddenFields: hf })}
          />

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.smsOptIn.enabled}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      smsOptIn: {
                        ...formData.smsOptIn,
                        enabled: e.target.checked,
                      },
                    })
                  }
                />
              }
              label="Enable SMS Opt-In"
            />
          </Grid>

          {formData.smsOptIn.enabled && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="SMS Consent Text"
                value={formData.smsOptIn.consentText}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    smsOptIn: {
                      ...formData.smsOptIn,
                      consentText: e.target.value,
                    },
                  })
                }
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      isActive: e.target.checked,
                    })
                  }
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
